import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BcmService } from '@modules/bcm/bcm.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmBaseFacade } from '@modules/bcm/@core/state-management/bcm-base.facade';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { ThemePalette } from '@angular/material/core';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { filteredListExportHeaderActionBase } from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import {
    BcmEmployee,
    BcmEmployeeAvailableUser,
    BcmEmployeeAvailableUserDto,
    BcmEmployeeDto,
    BcmEmployeeLicenseUsageDto,
    BcmEmployeeProfileDto,
} from '@shared/models/bcm-employee';
import { employeesTableData } from '@modules/bcm/@core/state-management/employees/employees-table-data';
import { removeRedundantDefaultActiveEmployeeFilter } from '@modules/bcm/@core/state-management/employees/employees-session-filter.util';
import { TablePredefinedFilter } from '@modules/bcm/@core/state-management/bcm-base.types';
import { DataFilterOperator } from '@core/datafilter/filter-operator';
import { DataFilterOperationType } from '@core/datafilter/filter-operation-type';
import { removePrimaryDepartmentFromAdditionalIds } from '@modules/bcm/employees/employee-additional-departments.util';
import { TolgeeService } from '@core/translation/tolgee.service';
import { parseHttpResponseList } from '@shared/functions/parse-http-response';
import { TranslationService } from '@core/translation/translation.service';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';

@Injectable({ providedIn: 'root' })
export class EmployeesFacade extends BcmBaseFacade<BcmEmployee, BcmEmployeeDto> {

    readonly resourcePath = 'employees';

    get resourceNameSingular(): string {
        return this._tolgee.translate('bcmEmployees.singular');
    }

    get resourceNamePlural(): string {
        return this._tolgee.translate('bcmEmployees.plural');
    }

    readonly resourceNamePluralTranslationKey = 'bcmEmployees.plural';

    readonly resourceNameSingularTranslationKey = 'bcmEmployees.singular';

    readonly addNewButtonTextTranslationKey = 'bcmEmployees.new';

    readonly resourceIconPlural = 'badge';

    readonly resourceIconSingular = 'badge';

    readonly deletePermission = BcmUserPermission.EMPLOYEES_DELETE;

    readonly readPermission = BcmUserPermission.EMPLOYEES_READ;

    readonly writePermission = BcmUserPermission.EMPLOYEES_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
    ];

    readonly rowActions = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (employee: BcmEmployee) => this._bcmNavigationService.navigate('employees/' + employee.id),
        },
    ];

    readonly predefinedFilter: TablePredefinedFilter<BcmEmployee>[];

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                bcmNavigationService: BcmNavigationService,
                private _bcmNavigationService: BcmNavigationService,
    ) {
        removeRedundantDefaultActiveEmployeeFilter();

        super(
            BcmEmployee,
            employeesTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService,
        );

        this.rowClickHandler = (employee: BcmEmployee) => {
            this._bcmNavigationService.navigate('employees/' + employee.id);
        };

        const isActiveColumn = employeesTableData.columnDefinitions.find(item => item.property === 'isActive');
        this.predefinedFilter = [
            {
                name: TranslationService.translate('bcmEmployees.filters.active'),
                icon: 'person',
                filters: undefined,
            },
            {
                name: TranslationService.translate('bcmEmployees.filters.inactive'),
                icon: 'person_off',
                filters: [
                    {
                        property: 'isActive',
                        operator: DataFilterOperator.Equal,
                        operationType: DataFilterOperationType.And,
                        value: false,
                        columnDefinition: isActiveColumn,
                    },
                ],
            },
            {
                name: TranslationService.translate('bcmEmployees.filters.all'),
                icon: 'groups',
                filters: [
                    {
                        property: 'isActive',
                        operator: DataFilterOperator.Equal,
                        operationType: DataFilterOperationType.And,
                        value: 'all',
                        columnDefinition: isActiveColumn,
                    },
                ],
            },
        ];
    }

    loadAvailableUsers(): Observable<BcmEmployeeAvailableUser[]> {
        return this.getList(`${this.resourcePath}/available-users`)
            .pipe(parseHttpResponseList<BcmEmployeeAvailableUserDto, BcmEmployeeAvailableUser>(BcmEmployeeAvailableUser));
    }

    loadAvailableUserProfile(userId: string): Observable<BcmEmployeeProfileDto> {
        return this.getRaw<{ profile: BcmEmployeeProfileDto }>(`${this.resourcePath}/available-users/${userId}/profile`)
            .pipe(map(response => response.profile));
    }

    loadLicenseUsage(): Observable<BcmEmployeeLicenseUsageDto> {
        return this.getRaw<BcmEmployeeLicenseUsageDto>(`${this.resourcePath}/license-usage`);
    }

    loadNextEmployeeNumber(): Observable<string> {
        return this.getRaw<{ employeeNumber: string }>(`${this.resourcePath}/next-number`)
            .pipe(map(response => response.employeeNumber));
    }

    buildPayloadFromForm(formValue: Record<string, unknown>): Record<string, unknown> {
        const payload: Record<string, unknown> = {
            user_id: formValue.user_id,
            employee_number: formValue.employee_number,
            primary_department_id: formValue.primary_department_id ?? null,
            entry_date: formValue.entry_date ?? null,
            exit_date: formValue.exit_date ?? null,
            workingTimeModelAssignments: formValue.workingTimeModelAssignments ?? [],
            internal_hourly_rate: formValue.internal_hourly_rate ?? null,
            annual_vacation_days: formValue.annual_vacation_days ?? null,
            business_email: formValue.business_email ?? null,
            business_mobile_phone: formValue.business_mobile_phone ?? null,
            business_landline_phone: formValue.business_landline_phone ?? null,
            additionalDepartmentIds: removePrimaryDepartmentFromAdditionalIds(
                formValue.additionalDepartmentIds as number[] | null | undefined,
                formValue.primary_department_id as number | null | undefined,
            ),
        };

        payload.position = formValue.position === 'department_head'
            ? 'department_head'
            : (formValue.position ?? null);

        return payload;
    }
}
